/**
 * SubscriptionExpiredPage — paywall de fin de trial ("bloquear hasta que pague").
 *
 * Se llega por el route guard (SubscriptionExpiredGuard) cuando la suscripción
 * está vencida. Muestra Starter y Profesional (sin pastilla de trial) con el
 * plan de la prueba preseleccionado y activa el cobro con Mercado Pago:
 *   updateSubscription(paymentMethodId 'pending') + activate MP → init_point.
 * Enterprise va por contacto con ventas. Solo OWNER/ADMIN pueden pagar; el
 * resto de los roles ve un aviso para pedirle al dueño que active el plan.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ShieldCheck, LogOut } from 'lucide-react';
import { getPlans } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useUserRole } from '../hooks/useUserRole';
import PlanCardsGrid from '../components/PlanCardsGrid';
import EnterpriseLeadForm from '../components/EnterpriseLeadForm';
import HelpIcon from '../components/help/HelpIcon';
import { getOfferedPlans, hasCustomPricing } from '../utils/plans';
import { canManageBilling } from '../utils/subscriptionStatus';
import { startMercadoPagoCheckout, isInvalidPayerEmailError } from '../utils/billingActions';
import { getPayerEmail } from '../utils/payerEmail';
import './SubscriptionExpiredPage.css';

const LOCALE_CURRENCY = { es: 'ARS', en: 'USD', pt: 'BRL' };

function SubscriptionExpiredPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, organization, logout } = useAuth();
  const { role } = useUserRole();
  const { subscription, isExpired, initialized, loading: subLoading } = useSubscription();

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState(
    subscription?.billingInterval === 'annual' ? 'annual' : 'monthly'
  );
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showEnterprise, setShowEnterprise] = useState(false);

  const currency =
    subscription?.currency || LOCALE_CURRENCY[(i18n.language || 'es').split('-')[0]] || 'ARS';
  const canPay = canManageBilling(role);

  const loadPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const response = await getPlans();
      // Cards self-service: Starter y Profesional. Enterprise va por ventas.
      const selfService = getOfferedPlans(response.plans).filter(
        (p) => !hasCustomPricing(p, currency)
      );
      setPlans(selfService);
      const trialPlan = selfService.find((p) => p.planId === subscription?.planId);
      const fallback = selfService.find((p) => p.planId === 'professional') || selfService[0];
      setSelectedPlanId((trialPlan || fallback)?.planId || null);
    } catch (err) {
      console.error('[SubscriptionExpiredPage] Error cargando planes:', err);
      setError(t('subscriptionExpired.errorLoadingPlans'));
    } finally {
      setLoadingPlans(false);
    }
  }, [currency, subscription?.planId, t]);

  useEffect(() => {
    if (canPay) loadPlans();
    else setLoadingPlans(false);
  }, [canPay, loadPlans]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleActivate = async () => {
    if (!selectedPlanId || !subscription?.subscriptionId || processing) return;
    try {
      setProcessing(true);
      setError(null);
      const initPoint = await startMercadoPagoCheckout({
        subscriptionId: subscription.subscriptionId,
        planId: selectedPlanId,
        billingInterval: billingCycle,
        currency,
        payerEmail: getPayerEmail(user, organization),
      });
      if (initPoint) {
        // URL externa de Mercado Pago (no es una ruta interna del router).
        window.location.href = initPoint;
        return;
      }
      setError(t('subscriptionExpired.errorActivating'));
    } catch (err) {
      console.error('[SubscriptionExpiredPage] Error activando plan:', err);
      setError(
        isInvalidPayerEmailError(err)
          ? t('subscription.payerEmailMissing')
          : err.response?.data?.message || t('subscriptionExpired.errorActivating')
      );
    } finally {
      setProcessing(false);
    }
  };

  // Si ya no está vencida (pagó / se refrescó), volver al panel.
  if (initialized && !subLoading && !isExpired) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="expired-page">
      <div className="expired-shell">
        <header className="expired-hero">
          <div className="expired-hero-icon" aria-hidden="true">
            <Lock size={28} />
          </div>
          <h1 className="expired-title">
            {t('subscriptionExpired.title')} <HelpIcon topicKey="subscription.expired" />
          </h1>
          <p className="expired-subtitle">{t('subscriptionExpired.subtitle')}</p>
          <p className="expired-data-safe">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>{t('subscriptionExpired.dataSafe')}</span>
          </p>
        </header>

        {canPay ? (
          <section className="expired-plans" aria-labelledby="expired-plans-title">
            <div className="expired-plans-header">
              <h2 id="expired-plans-title">{t('subscriptionExpired.choosePlan')}</h2>
              <div className="expired-billing-toggle" role="group">
                <button
                  type="button"
                  className={billingCycle === 'monthly' ? 'active' : ''}
                  onClick={() => setBillingCycle('monthly')}
                  aria-pressed={billingCycle === 'monthly'}
                >
                  {t('planSelection.monthly')}
                </button>
                <button
                  type="button"
                  className={billingCycle === 'annual' ? 'active' : ''}
                  onClick={() => setBillingCycle('annual')}
                  aria-pressed={billingCycle === 'annual'}
                >
                  {t('planSelection.annual')}
                </button>
              </div>
            </div>

            {loadingPlans ? (
              <p className="expired-loading">{t('common.loading')}</p>
            ) : (
              <PlanCardsGrid
                plans={plans}
                selectedPlanId={selectedPlanId}
                onSelect={(id) => {
                  setSelectedPlanId(id);
                  setError(null);
                }}
                billingCycle={billingCycle}
                currency={currency}
                showTrial={false}
              />
            )}

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <div className="expired-actions">
              <button
                type="button"
                className="btn btn-primary expired-cta"
                onClick={handleActivate}
                disabled={!selectedPlanId || processing || loadingPlans}
              >
                {processing
                  ? t('subscriptionExpired.activating')
                  : t('subscriptionExpired.activate')}
              </button>
              <p className="expired-enterprise">
                {t('subscriptionExpired.enterpriseQuestion')}{' '}
                <button
                  type="button"
                  className="expired-link"
                  onClick={() => setShowEnterprise(true)}
                >
                  {t('subscriptionExpired.contactSales')}
                </button>
              </p>
            </div>
          </section>
        ) : (
          <section className="expired-ask-owner" role="status">
            <p>{t('subscriptionExpired.askOwner')}</p>
          </section>
        )}

        <footer className="expired-footer">
          <button type="button" className="btn btn-ghost" onClick={handleLogout}>
            <LogOut size={16} aria-hidden="true" /> {t('subscriptionExpired.logout')}
          </button>
        </footer>
      </div>

      {showEnterprise && (
        <div className="modal-overlay" onClick={() => setShowEnterprise(false)}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('planSelection.enterpriseContactTitle')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowEnterprise(false)}
                aria-label={t('common.cancel')}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <EnterpriseLeadForm onClose={() => setShowEnterprise(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionExpiredPage;
